import React from "react";

import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";

import DataTable from "../../ui/Table";
import { ALL_PAGES, getRoleColor } from "../../auth/roles";

function RoleTable({
  roles,
  loading,
  onEdit,
  onDelete,
  selectedIds = [],
  onSelectionChange
}) {
  const pageLabel = (pageKey) => ALL_PAGES.find((p) => p.key === pageKey)?.label || pageKey;

  const sortedRoles = [...roles].sort((a, b) => a.role_id - b.role_id);

  const columns = [
    {
      key: "role_name",
      label: "Role Name",
      render: (role) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              flexShrink: 0,
              background: getRoleColor(role.role_name).color
            }}
          />

          {role.is_system && (
            <Tooltip title="Built-in role">
              <AdminPanelSettingsOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />
            </Tooltip>
          )}

          <Typography fontSize={14} fontWeight={600} sx={{ textTransform: "capitalize" }}>
            {role.role_name.replace(/_/g, " ")}
          </Typography>
        </Box>
      )
    },
    {
      key: "pages",
      label: "Pages",
      render: (role) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 500 }}>
          {role.pages.length === 0 && (
            <Typography fontSize={12} color="textSecondary">No pages assigned</Typography>
          )}

          {role.pages.map((page) => (
            <Tooltip
              key={page.page_key}
              title={`${page.data_scope === "own" ? "Own data" : "All data"} \u2022 ${page.access_level === "read" ? "Read only" : "Write access"}`}
            >
              <Chip
                label={pageLabel(page.page_key)}
                size="small"
                sx={{
                  fontWeight: 500,
                  fontSize: 12,
                  background: page.access_level === "read" ? "#f1f5f9" : "#eff6ff",
                  color: page.access_level === "read" ? "#475569" : "#2563eb"
                }}
              />
            </Tooltip>
          ))}
        </Box>
      )
    },
    {
      key: "actions",
      label: "Actions",
      width: 110,
      render: (role) => (
        <Box display="flex" justifyContent="flex-end">
          <IconButton onClick={() => onEdit(role)} sx={{ background: "#eff6ff", mr: 1 }}>
            <EditIcon fontSize="small" color="primary" />
          </IconButton>

          {role.is_system ? (
            <Tooltip title="Built-in roles can't be deleted">
              <span>
                <IconButton disabled>
                  <LockOutlinedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton onClick={() => onDelete(role)} sx={{ background: "#fee2e2" }}>
              <DeleteIcon fontSize="small" sx={{ color: "#dc2626" }} />
            </IconButton>
          )}
        </Box>
      )
    }
  ];

  return (
    <DataTable
      columns={columns}
      rows={sortedRoles}
      loading={loading}
      rowKey={(role) => role.role_id}
      selectable
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      isRowSelectable={(role) => !role.is_system}
      rowSelectableTooltip={() => "Built-in roles can't be selected for deletion"}
      emptyState={{
        icon: AdminPanelSettingsOutlinedIcon,
        title: "No roles found",
        subtitle: "Try changing search text or add a new role to control page access."
      }}
    />
  );
}

export default RoleTable;
